/**
 * The enrolment funnel's state machine: which surface is showing, what the API said, and the write.
 *
 * Everything stateful about joining lives here so that JoinBlock is only about rendering. The
 * library's blocks report that a control was pressed and nothing else -- the enrolment state, the
 * advertised bonus, the consent flag and the write are all the host's.
 *
 * Originally inline in JoinBlock, itself ported from the library's own reference host
 * (apps/demo/src/joinHero.tsx at 88710b0).
 */

import { useEffect, useState } from 'preact/hooks';
import { joinFailureCode } from '@drewling/urc-loyalty-api';
import { useLoyaltyClient } from '@drewling/urc-loyalty-ui';
import { describeLoyaltyError } from './errorDetail';
import { login } from './urcAuth';
import { setLoyaltyConsentGranted } from './consent';
import { withDeadline } from './deadline';
import { checkBeforeRejoin, createBrowserWithdrawal } from './withdrawal';
import { membership } from './membership';

/**
 * How long a whole join may take, from the click to the processor's answer, when the page's config
 * does not say (`urcLoyalty.consent.joinDeadlineMs`, from `consent.join_deadline_ms`).
 *
 * It has to cover the rejoin check (10s), the consent write (`write_deadline_ms`) and the library's
 * own retry schedule for a brand-new fan (six attempts 5s apart while the processor provisions
 * them). Before 2026-09-25 there was no bound at all, and the library's worst case alone was about
 * 25s of pauses plus six 10s request timeouts in front of a spinner.
 */
const JOIN_DEADLINE_MS = 60000;

/**
 * @return {number} The join's deadline in milliseconds.
 */
function joinDeadlineMs() {
	const consent = ( typeof window !== 'undefined' && window.urcLoyalty?.consent ) || {};

	return Number( consent.joinDeadlineMs ) || JOIN_DEADLINE_MS;
}

/**
 * The rejection for a join transaction the server did not move to its 3Halves leg: the consent
 * write was refused or undone (Auth0 down, rate-limited), or another tab's leave overtook it.
 * Named `LoyaltyConsent…` so the catch below labels it as the consent step, which it is.
 *
 * @param {Object} begun What `membership().begin()` answered.
 * @return {Error} The rejection.
 */
function membershipRefusal( begun ) {
	const state = begun.membership ? begun.membership.state : 'none';
	const error = new Error(
		`The join transaction did not start (${ state }, ${ begun.reason || 'ok' }).`
	);

	error.name = 'LoyaltyConsentMembershipError';

	return error;
}

/**
 * The rejection for a 3Halves leg that did not finish while the fan waited. The transaction has
 * already been reported as refused or aborted, so the server is undoing the consent write.
 *
 * @param {Object} out The leg's result.
 * @return {Error} The rejection.
 */
function legFailure( out ) {
	const error = new Error( `The join did not complete at the processor (${ out.code }).` );

	error.name =
		out.code === 'account_unavailable' ? SUPPRESSED_REFUSAL : 'LoyaltyMembershipLegError';
	error.code = out.code;

	return error;
}

/**
 * Stop a chain whose deadline has already passed, before its next step goes out.
 *
 * Aborting the signal cancels the request in flight, but a step that was still waiting -- the
 * consent write, say -- resolves later and would carry on into the cancel and the join for a fan
 * who has already been told it failed.
 *
 * @param {AbortSignal|undefined} signal The deadline's signal.
 * @return {void}
 */
function unlessAborted( signal ) {
	if ( signal && signal.aborted ) {
		throw Object.assign( new Error( 'The join ran out of time before this step.' ), {
			timedOut: true,
		} );
	}
}

/**
 * Begin refusals that mean the fan must sign in again: the endpoint refused the token (its only 401)
 * or there was no token to send. Never an outage: those answer 503 and keep the inline message.
 */
const REAUTH_REASONS = [ 'consent_unauthenticated', 'no-credential' ];

/**
 * The name of the error a rejoin stopped for suppression rejects with. A defensive branch: 3Halves
 * report the refusal removed (see ./withdrawal.js SUPPRESSED).
 */
const SUPPRESSED_REFUSAL = 'LoyaltySuppressed';

/**
 * The rejection for a rejoin the processor has already refused, so it travels the join's own error
 * path -- same inline message, same log line -- without a single write having been made.
 *
 * The inline message is the dialog's generic one, which is not ideal for a state no retry clears;
 * the library's confirm dialog takes one error label and a dedicated one is a change there.
 *
 * @param {Object|null} refusal The processor's own envelope, for the log.
 * @return {Error} The rejection.
 */
function suppressedRefusal( refusal ) {
	const error = new Error( 'The loyalty processor refuses this identity (account_unavailable).' );

	error.name = SUPPRESSED_REFUSAL;
	error.refusal = refusal;

	return error;
}

/**
 * @param {boolean} signedIn Whether there is a live loyalty bearer for this fan. Every read below
 *                           is token-scoped, so this decides whether any of them happen. Note this
 *                           is the SESSION's answer, not `useSignedIn()`'s -- JoinBlock passes
 *                           `session?.authenticated === true`.
 * @return {Object} The funnel's state and the four actions that move it.
 */
export function useJoinFunnel( signedIn ) {
	const client = useLoyaltyClient();

	// Exactly one surface is ever showing, so a two-panel steady state is unrepresentable. That
	// also keeps each transition inside a single render: Modal's scroll-lock is overlay-stack state
	// -- written to documentElement, not body, since @drewling/urc-loyalty-ui 130adc5 -- and a gap between
	// closing one overlay and opening the next unlocks the page mid-handover.
	const [ phase, setPhase ] = useState( 'hero' );

	// null until the read settles -- distinct from every answer the route can give.
	const [ enrolment, setEnrolment ] = useState( null );
	// The advertised welcome bonus. Deliberately NOT seeded with a number: a placeholder figure on
	// a surface whose whole job is to promise one is the defect this read exists to remove (D-041).
	const [ bonusPoints, setBonusPoints ] = useState( null );
	const [ startingTier, setStartingTier ] = useState( 'Starter' );
	const [ result, setResult ] = useState( null );
	const [ consented, setConsented ] = useState( false );
	const [ submitting, setSubmitting ] = useState( false );
	const [ submitError, setSubmitError ] = useState( false );

	useEffect( () => {
		if ( ! signedIn ) {
			// All three reads are token-scoped and reject before the wire without a session, so
			// asking costs three doomed calls to learn what the session already said. `signedIn` is
			// in the dependency list, so a sign-in that completes in another tab runs them for the
			// first time with no reload -- `useLoyaltySession` re-reads on every change of the fan,
			// and `client` changes on an account switch, so both cues are covered.
			return undefined;
		}

		let alive = true;

		// allSettled, not all: these fail independently, and a stale token that 403s /users/me
		// must not also cost us the enrolment read.
		Promise.allSettled( [
			client.getEnrolmentStatus(),
			client.getProfile(),
			client.getJoinBonusPoints(),
		] ).then( ( [ status, profile, bonus ] ) => {
			if ( ! alive ) {
				return;
			}

			// The whole state is kept, not a boolean: 'unconfigured' is not a kind of "not enrolled".
			if ( status.status === 'fulfilled' ) {
				setEnrolment( status.value.state );
			}

			if ( profile.status === 'fulfilled' ) {
				setStartingTier( profile.value.tierName );
			}

			if ( bonus.status === 'fulfilled' ) {
				setBonusPoints( bonus.value );
			}

			// allSettled swallows rejections, and every one of these degrades to something that
			// looks like a design choice on screen -- a dialog with no points chip, a tier that is
			// really just the 'Starter' seed. Say so once, so that "the join failed" and "nothing
			// could be read in the first place" are distinguishable without a debugger.
			const failed = { enrolment: status, profile, bonus };

			Object.keys( failed )
				.filter( key => failed[ key ].status === 'rejected' )
				.forEach( key => {
					console.warn(
						`[loyalty] join hero could not read ${ key }`,
						describeLoyaltyError( failed[ key ].reason ),
						failed[ key ].reason
					);
				} );
		} );

		return () => {
			alive = false;
		};
	}, [ client, signedIn ] );

	const openConfirm = () => {
		setConsented( false );
		setSubmitError( false );
		setPhase( 'confirm' );
	};

	const closeConfirm = () => {
		setPhase( 'hero' );
	};

	const submit = () => {
		setSubmitting( true );
		setSubmitError( false );

		/*
		 * Consent first, then join -- and the ordering is structural rather than a rule to check.
		 *
		 * `joinLoyalty` lives inside the write's `.then`, so a rejected consent write means the
		 * join is never attempted: there is no flag anybody could forget to look at. The record in
		 * Auth0 is therefore never behind the enrolment, which is the direction that matters. It can
		 * be AHEAD -- a written consent followed by a failed join leaves a fan consented and not
		 * enrolled -- and that is the accepted trade, because the fan did consent and the retry is
		 * the join.
		 *
		 * `setLoyaltyConsentGranted` costs no request for a fan whose consent is already confirmed
		 * at this value, so a repeat join and a double-click are both free.
		 *
		 * `consentToLoyalty: true` on the join stays. That is 3Halves' own consent field, typed as
		 * the literal `true`, recording acceptance of the loyalty terms on their side; ours is an
		 * independent record in Auth0. Both are now written in one click, and the API's own
		 * `consent_unavailable` join failure refers to THEIR write, never to this one.
		 *
		 * `joinLoyalty` is exempt from ./consentGate.js, so the gate cannot deadlock this call
		 * against a consent read that has not settled.
		 *
		 * **The cancel in the middle is not housekeeping** (added 2026-09-22, with the opt-out). A
		 * fan who leaves and then rejoins leaves a withdrawal request standing at 3Halves, and that
		 * request is executed by an admin by hand, whenever they get to it -- so without this, a fan
		 * who is back in the programme can be archived weeks later on the strength of a decision
		 * they reversed. With `erase_data` on it is worse than archiving: the fan is erased. Until
		 * 2026-09-27 the suppression that followed barred their identity from the programme for
		 * good; 3Halves report that removed (#660, not measured here), so such a fan now comes back
		 * as a brand-new member with none of what they had. Their Auth0 record by then says
		 * `loyaltyoptin: true` and carries no withdrawal date, so nothing else in either system
		 * would contradict it.
		 *
		 * Best effort, and it must stay best effort: it never rejects, and its failure must not
		 * cost the fan a join -- a gateway that is down or refusing must not keep a fan out of the
		 * programme. (Until 2026-09-30 it refused every deployed origin's preflight but dev's.)
		 *
		 * **The read that finds the id comes FIRST, before the consent write, and that is its own
		 * decision** (added 2026-09-24, D-091). As 3Halves described it then, a fan whose erasure
		 * had completed was SUPPRESSED by the processor for good while their Auth0 account could
		 * still sign in -- so they could reach this dialog, and writing `loyaltyoptin: true` for
		 * them would re-open this site's collection for an erased fan and leave our record
		 * contradicting the processor's, all for a join certain to be refused.
		 * `checkBeforeRejoin()` asks first; a suppressed answer writes nothing. **That branch is
		 * defensive now:** 3Halves report the refusal removed on 2026-09-27 (#660), which is not
		 * measured here, and it fires only on the explicit code. The read itself stays either way:
		 * it is the lookup the cancel used to make on its own, so the request count is unchanged,
		 * and it is only a read, so "our record first" still holds for every state change. It
		 * fails open: where the read cannot land, the rejoin proceeds as before.
		 */
		const withdrawal = createBrowserWithdrawal();
		const journal = membership();

		/*
		 * **Since 2026-09-25 the join is a journal transaction** (App\Loyalty\Membership, D-093),
		 * and what the long comment above describes is the LEGACY path, kept for a page cached
		 * before the journal's routes and for an install whose table is not installed yet.
		 *
		 * What changes: the begin writes a durable row and makes the consent write inline, under the
		 * server's per-fan lock, restoring what was there if it cannot confirm. The 3Halves leg
		 * (./membership/processorLeg.js) then runs status → enrol if needed → cancel any open
		 * withdrawal, with a fence before every call -- and the cancel moves AFTER the enrolment,
		 * so an aborted join never leaves an enrolled fan with no withdrawal standing. Any failure
		 * the fan is shown is reported as `abort` or `refused`, and the server undoes the consent
		 * write: the fan ends fully out, not consented-and-not-enrolled as the legacy path leaves
		 * them. D-091's pre-check still runs first, unchanged.
		 */
		// The transaction this attempt began, until it has been reported. Aborted on any failure,
		// so a begin whose answer arrived after the deadline is undone rather than left standing.
		let pending = '';

		const legacyJoin = ( before, signal ) =>
			setLoyaltyConsentGranted( true )
				.then( () => {
					unlessAborted( signal );

					return before.requestId ? withdrawal.cancel( before.requestId ) : null;
				} )
				.then( () => {
					unlessAborted( signal );

					return client.joinLoyalty( { consentToLoyalty: true }, { signal } );
				} );

		const journalJoin = async ( before, signal ) => {
			const begun = await journal.begin( 'join' );

			if ( begun.fallback ) {
				return legacyJoin( before, signal );
			}

			// Only a transaction the server may actually hold is aborted on failure. A begin it
			// refused outright -- a 401, a 429, a 503 before the insert -- created nothing.
			pending = begun.mayExist ? begun.txn : '';

			if ( ! begun.ok && REAUTH_REASONS.includes( begun.reason ) ) {
				// The server refused the fan's token, or there was none to send: a lapsed session,
				// and the same verdict the legacy path's LoyaltyConsentAuthError carries. Only a
				// 401 means this -- an outage answers 503 and gets the inline message.
				throw Object.assign( new Error( 'The loyalty session has lapsed.' ), {
					name: 'LoyaltyConsentMembershipError',
					requiresReauth: true,
				} );
			}

			if ( ! begun.ok || begun.membership.state !== 'processor_pending' ) {
				throw membershipRefusal( begun );
			}

			unlessAborted( signal );

			const out = await journal.run( begun.membership, {
				signal,
				wait: true,
				mustVerifyWithdrawal: Boolean( before.requestId ),
			} );

			if ( ! out || out.skipped ) {
				throw Object.assign( new Error( 'Another tab held this fan’s membership leg.' ), {
					timedOut: true,
				} );
			}

			if ( signal && signal.aborted ) {
				// The dialog has already shown the failure and its catch has sent the abort. A
				// `done` sent now would commit a join the fan was told did not happen -- so it is
				// not sent, and the undo stands (flagged, since the processor may hold them).
				throw Object.assign( new Error( 'The join finished after its deadline.' ), {
					timedOut: true,
				} );
			}

			if ( out.result === 'done' ) {
				await journal.report( begun.txn, out );
				pending = '';

				if ( begun.membership.after === 'leave' || before.requestId ) {
					journal.noteRejoin();
				}

				// A fan the processor already had -- a rejoin before the executor ran -- makes no
				// join call and so has no write result. Nothing was credited, so nothing is
				// celebrated (D-047).
				return out.joined || { state: 'enrolled', bonusAwarded: false, bonusPoints: 0 };
			}

			// Through abort() rather than a plain report: it is kept until the server answers for
			// it, so a failure the fan was shown is undone even when this report is lost -- a
			// later page resends it rather than finishing the join.
			pending = '';
			await journal.abort( begun.txn, out.code || out.result );

			throw legFailure( out );
		};

		// One deadline over the whole chain, and its signal reaches the join itself -- the library
		// honours `opts.signal`, including during its 5s retry pauses. See JOIN_DEADLINE_MS.
		withDeadline(
			signal =>
				checkBeforeRejoin( withdrawal ).then( before => {
					if ( before.suppressed ) {
						throw suppressedRefusal( before.refusal );
					}

					unlessAborted( signal );

					return journal.available()
						? journalJoin( before, signal )
						: legacyJoin( before, signal );
				} ),
			joinDeadlineMs()
		)
			.then( joined => {
				// From the write's own answer, which no re-read can race. Unconditional: both
				// branches below need it.
				setEnrolment( joined.state );

				// A repeat join is not a join, and it gets no celebration (D-047).
				//
				// `bonusAwarded` is false when THIS call credited nothing, which the hero's
				// fail-open CTA makes reachable: a fan whose enrolment read failed is offered a
				// join they have already made, and the route accepts it idempotently. The success
				// screen would then announce "+0" over an account that already holds its points --
				// and unlike JoinConfirmModal's points chip, that figure cannot be hidden, because
				// LoyaltyJoinSuccess.welcomePoints is required (D-044). `state` is 'enrolled' by
				// now, so falling back to the hero shows exactly what a fan in that state sees on
				// a cold load, which is the honest answer.
				//
				// NOT `alreadyEnrolled`: @drewling/urc-loyalty-api documents that field as on the wire but
				// unreliable, which is why it names bonusAwarded "whether THIS call credited".
				if ( ! joined.bonusAwarded ) {
					setPhase( 'hero' );
					return;
				}

				// One commit: the dialog's `open` goes false and the success surface mounts in the
				// same render, so the overlay's lock is released as it unmounts rather than handed
				// back and forth.
				setResult( joined );
				setPhase( 'success' );
			} )
			.catch( error => {
				if ( pending ) {
					// Best effort and never rejects: the server undoes whatever the begin wrote.
					journal.abort( pending, error && error.timedOut ? 'deadline' : 'dialog' );
					pending = '';
				}

				// The dialog stays open with its inline message -- a fan must never be left
				// believing a failed join succeeded.
				//
				// The code alone is not a diagnosis. joinFailureCode() names only the
				// rejections the join route documents -- six since `0.9.0` -- and returns null for
				// everything else, so logging just it reports "unknown" for every auth failure,
				// timeout and 5xx -- the only cases anyone actually needs to debug. The summary
				// says which, and the raw error goes with it so DevTools can walk its `cause`.
				//
				// A `not_provisioned` here means the client's whole retry budget ran out (six
				// attempts, 5s apart, absorbed inside joinLoyalty since `0.9.0`), so it reads as
				// "still not ready, worth offering again in a minute" rather than a dead end.
				const code = joinFailureCode( error );

				// A consent failure names itself, because joinFailureCode() answers null for it and
				// the summary would otherwise read as an undiagnosable join failure -- when in fact
				// no join was even attempted.
				const consent =
					error && String( error.name || '' ).startsWith( 'LoyaltyConsent' )
						? ` (consent write: ${ error.name })`
						: '';

				// Likewise the suppressed fan, stopped before anything was written or sent.
				const suppressed =
					error && error.name === SUPPRESSED_REFUSAL
						? 'account_unavailable (suppressed at the processor; nothing was written)'
						: '';

				console.warn(
					`[loyalty] join failed: ${
						code || suppressed || 'not a documented join failure'
					}${ consent }`,
					describeLoyaltyError( error ),
					error
				);

				/*
				 * An auth failure is not a join failure, and an inline "something went wrong" is
				 * the wrong answer to it: the fan can do nothing about it from inside this dialog.
				 * `requiresReauth` is LoyaltyAuthError's own verdict on whether retrying could ever
				 * work -- true both when the token provider had nothing to give and when the API
				 * rejected what it was given -- so the honest response is a trip through Auth0,
				 * taken now that it is actually known to be needed. Read defensively rather than by
				 * instanceof, for the reason errorDetail.js gives.
				 *
				 * **The case that used to dominate this branch is gone rather than covered.** A
				 * live credential deleted underneath us mid-consent-write -- the silent probe's
				 * `login_required` clear, firing on `focus` inside the write's ~2.5s round trip --
				 * is what `pinCredential()` existed for (D-074). Nothing clears a cookie jar now
				 * and there is no probe, so what reaches here is only what should: a token the API
				 * itself refused, or one that genuinely expired between the click and the join.
				 */
				if ( error && error.requiresReauth === true ) {
					login( window.location.href );

					return;
				}

				setSubmitError( true );
			} )
			.finally( () => {
				setSubmitting( false );
			} );
	};

	return {
		phase,
		enrolment,
		// The standing offer, for the confirm dialog. undefined rather than null because that is
		// what the library's optional prop reads as "no figure to show".
		bonusPoints: bonusPoints === null ? undefined : bonusPoints,
		// What the success screen names: the write's own figure wherever there is one, the standing
		// offer otherwise. null until one of the two is in hand, because a celebration naming a
		// number nobody promised is exactly what getJoinBonusPoints() removed. `result` is set only
		// for a join that actually credited, so this is never the 0 a repeat join answers with.
		credited: result === null ? bonusPoints : result.bonusPoints,
		startingTier,
		consented,
		submitting,
		submitError,
		openConfirm,
		closeConfirm,
		setConsented,
		submit,
	};
}
